import type { GtmItem } from "./types";

/** The line-item fields the mappers read — structurally satisfied by both a
 * cart `SelectionItemModel` and an order line (same Centra model). */
type SelectionItemLike = {
	sku?: string;
	size?: string;
	quantity?: number;
	priceEachAsNumber?: number;
	product?: {
		name?: string;
		sku?: string;
		brand?: string;
		brandName?: string;
		variantName?: string;
		categoryName?: string[];
	};
};

/** Most-specific (leaf) label from a Centra category path. */
export const categoryLabel = (categories?: string[] | null): string | undefined =>
	categories?.filter(Boolean).at(-1) ?? undefined;

/** "Colour / Size" when both are known, otherwise whichever is present. */
export const variantLabel = (variant?: string | null, size?: string | null): string | undefined => {
	const v = variant?.trim();
	const s = size?.trim();
	if (v && s) return `${v} / ${s}`;
	return v || s || undefined;
};

/**
 * Map a cart or order line item to a GA4 `items[]` entry. `item_id` is the
 * variant/size SKU — the same identifier the Depict events send, keeping
 * attribution consistent across both analytics stacks.
 */
export const selectionItemToGtmItem = (item: SelectionItemLike): GtmItem => {
	const product = item.product;
	return {
		item_id: item.sku ?? product?.sku ?? "",
		item_name: product?.name ?? "",
		item_brand: product?.brandName ?? product?.brand ?? undefined,
		item_variant: variantLabel(product?.variantName, item.size),
		item_category: categoryLabel(product?.categoryName),
		price: item.priceEachAsNumber,
		quantity: item.quantity ?? 1,
	};
};
